import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { SfAccountSnapshot, SfInvoiceOpportunity, AccountStateInfo } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AccountStatusBadge } from '@/components/AccountStatusBadge';
import { UpcomingChargesCard } from '@/components/UpcomingChargesCard';
import { InvoiceHistoryTable } from '@/components/InvoiceHistoryTable';
import { LifecycleCard } from '@/components/LifecycleCard';
import { formatDate, formatDateOnly } from '@/lib/utils';
import { ArrowLeft, Loader2, ExternalLink } from 'lucide-react';

export function AccountDetailPage() {
  const { sfAccountId } = useParams<{ sfAccountId: string }>();
  const [account, setAccount] = useState<SfAccountSnapshot | null>(null);
  const [lifecycle, setLifecycle] = useState<AccountStateInfo | null>(null);
  const [upcomingCharges, setUpcomingCharges] = useState<SfInvoiceOpportunity[]>([]);
  const [invoiceHistory, setInvoiceHistory] = useState<SfInvoiceOpportunity[]>([]);
  const [provisioningRequest, setProvisioningRequest] = useState<{ id: string; status: string; created_at: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sfAccountId) {
      loadAccount();
    }
  }, [sfAccountId]);

  const loadAccount = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getAccount(sfAccountId!);
      setAccount(response.account);
      setLifecycle(response.lifecycle);
      setUpcomingCharges(response.upcoming_charges);
      setInvoiceHistory(response.invoice_history);
      setProvisioningRequest(response.provisioning_request);
    } catch (err) {
      console.error('Failed to load account:', err);
      setError('Failed to load account details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLifecycleActionComplete = () => {
    // Reload account data after a lifecycle action
    loadAccount();
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
        </div>
      </Layout>
    );
  }

  if (error || !account) {
    return (
      <Layout>
        <div className="text-center py-24">
          <p className="text-red-600 mb-4">{error || 'Account not found'}</p>
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Accounts
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{account.account_name}</h1>
          </div>
          <AccountStatusBadge status={account.account_status_ai} />
        </div>

        {provisioningRequest && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-700">
            <div className="flex items-center justify-between">
              <span>
                This account was provisioned via request on {formatDate(provisioningRequest.created_at)}
              </span>
              <Link to={`/requests/${provisioningRequest.id}`}>
                <Button variant="ghost" size="sm" className="text-blue-700 hover:text-blue-800">
                  View Request
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Account Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Abby Assigned Phone Number</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {account.assigned_phone_number || 'Not assigned'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Billing Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {account.billing_date ? `Day ${account.billing_date}` : 'Not set'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">AI Minutes Used (Current Cycle)</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {account.ai_minutes_used_current_cycle !== null
                        ? account.ai_minutes_used_current_cycle.toLocaleString()
                        : 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Last Synced</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(account.last_synced_at)}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Contract Information</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">AI Contract Start</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {account.ai_contract_start ? formatDateOnly(account.ai_contract_start) : 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">AI Contract End</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {account.ai_contract_end ? formatDateOnly(account.ai_contract_end) : 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Free Trial Start</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {account.ai_free_trial_start ? formatDateOnly(account.ai_free_trial_start) : 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Free Trial End</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {account.ai_free_trial_end ? formatDateOnly(account.ai_free_trial_end) : 'N/A'}
                    </dd>
                  </div>
                  {account.ai_free_trial_cancelled && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Trial Cancellation Date</dt>
                      <dd className="mt-1 text-sm text-red-600">
                        {formatDateOnly(account.ai_free_trial_cancelled)}
                      </dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>

            <InvoiceHistoryTable invoices={invoiceHistory} />
          </div>

          <div className="space-y-6">
            {/* Lifecycle Card */}
            {lifecycle && (
              <LifecycleCard
                account={account}
                lifecycle={lifecycle}
                onActionComplete={handleLifecycleActionComplete}
              />
            )}

            <UpcomingChargesCard charges={upcomingCharges} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
